import { useRef, useMemo } from 'react'
import QRCode from 'qrcode.react'
import { Avatar, Popover, Space, Tooltip, Typography, Divider } from '@arco-design/web-react'
import useLocale from '@/utils/useLocale'
import { IconPlus } from '@arco-design/web-react/icon'
import IconRefresh from '@/assets/ic_header_Refresh.png'
import IconLink from '@/assets/ic_header_Link.png'
import IconQrcode from '@/assets/ic_header_Qrcode.png'
import styles from './index.module.less'
import TabsModule, { TabItem } from '@/components/TabsModule'
import useCollectBtn from '@/hooks/useCollectBtn'
import Image from 'next/image'
import useShareBtn from '@/hooks/useShareBtn'
import usePageNavPosition from '@/hooks/usePageNavPosition'

type Props = {
  onRefresh: () => Promise<void>
  tabsList: TabItem[]
  activeTab: string
  setActiveTab: (tab: string) => void
  collectBtnHook: ReturnType<typeof useCollectBtn>
  name: string
  link: string
  images: string[]
}

function PageNav({ onRefresh, tabsList, activeTab, setActiveTab, collectBtnHook, name, link, images }: Props) {
  const t = useLocale()

  const imgUrl = images?.[0]
  const navRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const shareBtnHook = useShareBtn()
  const { contentWidth, leftMenuWidth } = usePageNavPosition({ contentRef })

  const currentSecondNav = useMemo(() => {
    return tabsList.find((tab) => tab.nodeName === activeTab)?.tabList || []
  }, [activeTab, tabsList])

  const handleSecondNavClick = (tab: any) => {
    const anchorName = document.getElementById(tab.nodeName)
    const container = document.getElementById('layout-content-wrapper')

    if (anchorName && container) {
      const containerRect = container.getBoundingClientRect()
      const anchorRect = anchorName.getBoundingClientRect()
      const scrollTop = container.scrollTop
      const targetScrollTop = scrollTop + anchorRect.top - containerRect.top - 64 - 30

      container.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div id="page-nav" className={styles.container} ref={navRef}>
      <div className={styles.content} ref={contentRef} style={{ width: contentWidth, left: leftMenuWidth }}>
        <div className={styles.contentNav}>
          <TabsModule
            className={styles['page-nav-tabs']}
            tabsList={tabsList.map(({ children, ...other }) => other)}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            scrollToTop
          />
          <div className={styles.secondContent}>
            <Space split={<Divider type="vertical" />}>
              {currentSecondNav.map((tab, index) => {
                return (
                  <span key={index} className={styles.secondNavItem} onClick={() => handleSecondNavClick(tab)}>
                    {tab.title}
                  </span>
                )
              })}
            </Space>
          </div>
        </div>

        <div className={styles['info-wrapper']}>
          <Space direction="horizontal" size={40}>
            <Space direction="horizontal" size={8}>
              <Avatar size={44} className={styles['info-avatar']}>
                {imgUrl ? <img src={imgUrl} alt={name} /> : <IconPlus />}
              </Avatar>
              <Space direction="vertical" size={0}>
                <div className={styles['info-wrapper-content']}>
                  <div className={styles['info-wrapper-text']}>{name}</div>
                  <Typography.Text
                    copyable={{
                      text: name
                    }}
                  ></Typography.Text>
                </div>
              </Space>
            </Space>
            <Space size={16}>
              {shareBtnHook.renderForNav()}
              {/* 刷新 */}
              <Tooltip mini content={t['product.detail.refresh']}>
                <div onClick={onRefresh} className={styles['action-btn']}>
                  <Image alt="refresh" src={IconRefresh?.src} width={18} height={18} />
                </div>
              </Tooltip>
              <Tooltip mini content={t['product.detail.tiktokLink']}>
                <div
                  className={styles['action-btn']}
                  onClick={() => {
                    window.open(`${link}`)
                  }}
                >
                  <Image alt="link" src={IconLink?.src} width={20} height={18} />
                </div>
              </Tooltip>
              <Tooltip mini content={t['product.detail.tiktokQrcode']}>
                <Popover
                  title=""
                  content={
                    <QRCode
                      value={link}
                      // 二维码的大小
                      size={100}
                      // 二维码的颜色
                      fgColor="#333333"
                      style={{ margin: 'auto' }}
                    />
                  }
                >
                  <div className={styles['action-btn']}>
                    <Image alt="qrcode" src={IconQrcode?.src} width={18} height={18} />
                  </div>
                </Popover>
              </Tooltip>

              {collectBtnHook.renderForNav({ getPopupContainer: () => navRef.current! })}
            </Space>
          </Space>
        </div>
      </div>
    </div>
  )
}

export default PageNav
